/**
 * NIRSALClimateImpact — Climate & Portfolio Impact
 * Route: /corporate/nirsal/climate
 * EESG framing: Environmental · Economic · Social · Governance
 */
import React from "react";
import { useNIRSAL } from "@/context/NIRSALContext";
import { PageHeader, SectionCard } from "@/components/ds";

const fmtN = (v, u = "") => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1_000_000) return `${(n/1e6).toFixed(1)}M ${u}`.trim(); if (n >= 1_000) return `${(n/1000).toFixed(0)}K ${u}`.trim(); return `${n} ${u}`.trim(); };
const fmtU = (v) => { if (!v) return "—"; const n = Number(v); if (n >= 1_000_000) return `$${(n/1e6).toFixed(1)}M`; if (n >= 1_000) return `$${(n/1000).toFixed(0)}K`; return `$${n}`; };

const MetricRow = ({ label, value, sub, accent }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-slate-50">
    <div><p className="text-xs font-medium text-slate-700">{label}</p>{sub && <p className="text-[10px] text-slate-400">{sub}</p>}</div>
    <p className={`text-sm font-black ${accent || "text-slate-900"}`}>{value}</p>
  </div>
);

export default function NIRSALClimateImpact() {
  const { consolidated, loading, selectedPeriod } = useNIRSAL();
  const p  = consolidated?.portfolio || {};
  const cl = consolidated?.clusters  || [];
  const total = p.total_reduction_tco2e_yr || 0;
  const cap   = p.total_capital_mobilized || 0;

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading climate data…</div>;

  const intensityCapital = cap > 0 ? (total / cap * 1_000_000).toFixed(2) : "—";
  const intensityCluster = cl.length > 0 ? (total / cl.length).toFixed(0) : "—";

  const byTech = p.by_technology || {};
  const byState= p.by_state || {};

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-6">
      <PageHeader label="CLIMATE & PORTFOLIO IMPACT" title="" />
      <p className="text-xs text-slate-400 -mt-4">EESG framing · Portfolio-level climate performance · IFRS S1/S2 aligned</p>

      {/* EESG summary cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Environmental", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0",
            rows: [["GHG Reduction", fmtN(total, "tCO₂e/yr")],["Waste Processed", fmtN(p.total_waste_processed_t_yr,"t/yr")],["Renewable Energy", fmtN(p.total_renewable_kwh_yr,"kWh/yr")],["Plastic Recovered", fmtN(p.total_plastic_recovered_t_yr,"t/yr")]] },
          { label: "Economic", color: "#1e3a5f", bg: "#eff6ff", border: "#bfdbfe",
            rows: [["Capital Mobilized", fmtU(cap)],["CRG Exposure", fmtU(p.total_crg_exposure)],["Leverage Ratio", `${(p.leverage_ratio||0).toFixed(1)}x`],["Carbon Value (Base)", fmtU(p.carbon_value_base_usd) + "/yr"]] },
          { label: "Social", color: "#d97706", bg: "#fffbeb", border: "#fde68a",
            rows: [["Beneficiaries", fmtN(p.total_beneficiaries)],["Jobs Created", fmtN(p.total_jobs_created)],["Women/Youth %", `${(p.women_youth_pct_avg||0).toFixed(1)}%`],["States Active", p.states_count || 0]] },
          { label: "Governance", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe",
            rows: [["DMRV Evidence Coverage", `${(consolidated?.evidencePct||0).toFixed(1)}%`],["Portfolio Readiness", `${consolidated?.readiness_score||"—"}/100`],["Art.6.4 Clusters", p.art6_4_clusters || 0],["IFRS S1/S2 Alignment", "In Progress"]] },
        ].map(({ label, color, bg, border, rows }) => (
          <div key={label} className="bg-white rounded-xl border shadow-sm p-4" style={{ borderColor: border }}>
            <div className="text-xs font-black mb-3 uppercase tracking-wider" style={{ color }}>{label}</div>
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs py-1 border-b border-slate-50">
                <span className="text-slate-500">{k}</span>
                <span className="font-bold text-slate-800">{v}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Intensity KPIs */}
      <SectionCard title="Climate Intensity Metrics">
        <div className="p-4 grid grid-cols-3 gap-4">
          {[
            ["tCO₂e per $1M deployed", intensityCapital, "Reduction intensity per unit capital"],
            ["tCO₂e per cluster", intensityCluster, "Average reduction per CPA"],
            ["Jobs per $1M deployed", cap > 0 ? ((p.total_jobs_created||0) / cap * 1_000_000).toFixed(1) : "—", "Employment intensity per unit capital"],
          ].map(([l, v, s]) => (
            <div key={l} className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-[11px] text-slate-500 mb-1">{l}</p>
              <p className="text-2xl font-black text-[#1e3a5f]">{v}</p>
              <p className="text-[10px] text-slate-400 mt-1">{s}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* By Technology */}
      {Object.keys(byTech).length > 0 && (
        <SectionCard title="Impact by Technology">
          <div className="overflow-x-auto p-2">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-100">
                <th className="text-left py-2 px-3 text-slate-400 font-medium">Technology</th>
                <th className="text-right py-2 px-3 text-slate-400 font-medium">Clusters</th>
                <th className="text-right py-2 px-3 text-slate-400 font-medium">Reduction (tCO₂e/yr)</th>
                <th className="text-right py-2 px-3 text-slate-400 font-medium">Capital Mobilized</th>
              </tr></thead>
              <tbody>
                {Object.entries(byTech).map(([t, d]) => (
                  <tr key={t} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium">{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</td>
                    <td className="py-2 px-3 text-right">{d.count}</td>
                    <td className="py-2 px-3 text-right text-emerald-700 font-bold">{fmtN(d.reduction)}</td>
                    <td className="py-2 px-3 text-right">{fmtU(d.capital)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
